/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useContext } from "react";
import { useMutation } from "@apollo/react-hooks";
import { isValidEmail } from "@tomocredit/common-fe/utils";
import ReferralMoney from "assets/boost/referral-money.webp";
import InfoSvg from "assets/info-black.svg";
import { SocialShares, Modal, BreakpointContext } from "components/common";
import { SEND_BOOST_REFERRAL } from "./BoostReferral";

const BoostReferralModal = ({
  email,
  isOpen,
  closeModal,
  referralCode,
  currentTerm,
  currentTier,
}) => {
  const [copyLinkText, setCopyLinkText] = useState("Copy");
  const [referralEmail, setReferralEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteButtonText, setInviteButtonText] = useState("Send");
  const [inviteErrorMessage, setInviteErrorMessage] = useState("");
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [sendBoostReferral] = useMutation(SEND_BOOST_REFERRAL);

  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");

  const BOOST_REFERRAL = `BOOST_${referralCode}`;
  const REFERRAL_LINK = `https://tomoboost.com/boost?referralCode=${BOOST_REFERRAL}&utm_source=webreferral`;

  const handleReferralLinkCopied = () => {
    navigator.clipboard.writeText(REFERRAL_LINK);
    if (typeof window?.gtag === "function") {
      window.gtag("event", "boost_referral_copied", {
        source: "web",
        tier: currentTier,
        term: currentTerm,
        email,
      });
    }
    setCopyLinkText("Copied");
    setTimeout(() => {
      setCopyLinkText("Copy");
    }, 5000);
  };

  const handleInviteButtonClicked = () => {
    if (loading) {
      return;
    }
    if (!isValidEmail(referralEmail)) {
      setInviteErrorMessage("Email address is required");
      return;
    }

    setLoading(true);
    setInviteErrorMessage("");
    setInviteButtonText("Sending");
    if (typeof window?.gtag === "function") {
      window.gtag("event", "boost_referral_invited", {
        source: "web",
        referrerTier: currentTier,
        referrerTerm: currentTerm,
        referrerEmail: email,
        invitedEmail: referralEmail,
      });
    }
    sendBoostReferral({
      variables: {
        email: referralEmail,
      },
    })
      .then(() => {
        setInviteButtonText("Sent");

        setTimeout(() => {
          setInviteButtonText("Send");
        }, 2000);
      })
      .catch((error) => {
        setInviteButtonText("Send");
        const message = error.graphQLErrors[0]?.message;
        setInviteErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      hasCrossContainer={false}
      options={{ showCloseButton: false }}
    >
      <div
        style={{ width: belowMd ? 364 : 524, height: belowMd ? 627 : 567 }}
        className="mx-5 my-10 flex flex-col justify-start items-center bg-white text-black rounded-xl overflow-hidden fade"
      >
        {/* TOP IMAGE WITH CLOSE BUTTON */}
        <div className="w-full relative" style={{ backgroundColor: "#F1EDFE" }}>
          <div
            style={{
              width: 25,
              height: 25,
            }}
            className="mt-4 mr-4 flex justify-center items-center absolute text-center text-black bg-gray-400 border rounded-full top-0 right-0 cursor-pointer text-4xl px-2 leading-none"
            onClick={closeModal}
          >
            &times;
          </div>
          <div
            className="flex justify-center items-center rounded-lg bg-success-200"
            style={{ height: 167 }}
          >
            <img
              src={ReferralMoney}
              alt=""
              className="w-auto"
              style={{ height: 88 }}
            />
          </div>
        </div>
        <div className="px-5 md:px-10 py-6 w-full" style={{ maxWidth: 564 }}>
          {showHowItWorks ? (
            <div className="md:px-4 space-y-2">
              <div className="font-bold text-2xl">How It Works</div>
              <div>
                Promo only applies to successful referrals through your
                individual referral code.
              </div>
              <div>Credits are automatically applied to your next payment.</div>
              <div>When your friend joins:</div>
              <div className="space-y-1">
                <div>
                  <span className="mr-1 font-bold">VIP = $100 off</span>($20 if
                  monthly)
                </div>
                <div>
                  <span className="mr-1 font-bold">Premium = $50 off</span>($10
                  if monthly)
                </div>
                <div>
                  <span className="mr-1 font-bold">Basic = $20 off</span>($5 if
                  monthly)
                </div>
              </div>
              <div className="py-8">
                <button
                  className="ml-4"
                  type="button"
                  onClick={() => setShowHowItWorks(false)}
                >
                  ← Back
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="font-bold text-2xl">
                Invite Friends, Get $100 Cash Each
              </div>
              <div className="mt-2">
                Earn up to $100 unlimited times with successful referrals.
              </div>
              <div className="mt-6">
                <div className="font-bold text-left">Share via link</div>
                <div className="mt-2 w-full flex justify-center items-center">
                  <input
                    disabled
                    type="text"
                    value={REFERRAL_LINK}
                    style={{
                      WebkitUserSelect: "auto",
                      paddingRight: 0,
                      minWidth: 250,
                    }}
                    className="placeholder-gray-600 text-black p-2 w-full outline-none bg-white rounded-md rounded-r-none border border-neutral-500 border-r-0"
                  />
                  <button
                    className="bg-boost-primary-500 text-white whitespace-nowrap text-center px-4 py-2 focus:outline-none rounded-l-none border border-boost-primary-500"
                    type="button"
                    onClick={handleReferralLinkCopied}
                  >
                    {copyLinkText}
                  </button>
                </div>
                <div className="mt-4">
                  <SocialShares size={24} className="flex" />
                </div>
              </div>
              <div className="mt-6">
                <div className="font-bold text-left">Invite via email</div>
                <div className="mt-2 w-full flex justify-center items-center">
                  <input
                    placeholder="Enter email"
                    className="placeholder-gray-600 text-black p-2 w-full outline-none bg-white rounded-md rounded-r-none border border-neutral-500 border-r-0"
                    value={referralEmail}
                    style={{
                      WebkitUserSelect: "auto",
                      paddingRight: 0,
                      minWidth: 250,
                    }}
                    onChange={(e) => {
                      setInviteErrorMessage("");
                      setReferralEmail(e.target.value);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-boost-primary-500 text-white whitespace-nowrap text-center px-4 py-2 focus:outline-none rounded-l-none border border-boost-primary-500"
                    onClick={handleInviteButtonClicked}
                  >
                    {inviteButtonText}
                  </button>
                </div>
                {inviteErrorMessage && (
                  <span className="text-left mt-2">{inviteErrorMessage}</span>
                )}
              </div>
              <div className="mt-6">
                <img src={InfoSvg} alt="" className="mr-1 inline w-4 h-4" />
                <button
                  type="button"
                  className="underline cursor-pointer focus:outline-none"
                  onClick={() => setShowHowItWorks(true)}
                >
                  Learn more
                </button>{" "}
                about referrals.
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BoostReferralModal;
